import { MessagesSquare } from 'lucide-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSidebar } from '../components/layout/SidebarContext';
import { ConversationView } from '../components/teamChat/ConversationView';
import { useI18n } from '../contexts/I18nContext';
import { useChatViewportLock } from '../hooks/useChatViewportLock';
import { useTeamChatConversations } from '../hooks/useTeamChat';

/**
 * /team-chat and /team-chat/:conversationId — internal Discord-like chat.
 * The Layout sidebar swaps to the conversation list on these routes; this
 * page renders the open conversation (or a pick-one empty state).
 */
export function TeamChatPage() {
  const { t } = useI18n();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { data: conversations, isLoading } = useTeamChatConversations();
  const { isMobile, openMobile } = useSidebar();

  // Same keyboard treatment as the AI assistant: without it, focusing the
  // composer on iOS pans the page up and throws the whole chat off-screen.
  useChatViewportLock();

  // On a phone the conversation list IS the sidebar overlay, so landing on
  // /team-chat with nothing open would show only the "pick a conversation"
  // hint and a hamburger. Slide the list in instead; picking one closes it
  // (Sidebar's onNavigate), and coming back to the bare route reopens it.
  useEffect(() => {
    if (isMobile && !conversationId) openMobile();
  }, [isMobile, conversationId, openMobile]);

  const conversation = (conversations ?? []).find((c) => c.id === conversationId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {conversation ? (
        <ConversationView key={conversation.id} conversation={conversation} />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
            <MessagesSquare size={26} className="text-muted-foreground" />
          </span>
          <h1 className="font-heading text-base font-semibold text-foreground">
            {t('sidebar.teamChat')}
          </h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            {conversationId && !isLoading ? t('teamChat.notFound') : t('teamChat.pickConversation')}
          </p>
        </div>
      )}
    </div>
  );
}
